import React, { useState } from "react";
import { useIntl } from "react-intl";
import { CreateMember, Role } from "@/model";
import { isEmail } from "@/util/stringUtil";
import { useCreateMember } from "@/query/member";
import { useScenarioId, useModal } from "@/hook";
import Button from "@/component/Button";
import { Modal } from "@/component/modal";
import { TextInputGroup } from "@/component/input";
import { errorToast, successToast } from "@/component/Toast";

import RoleDropDown from "./RoleDropDown";


export const MEMBER_ADD_MODAL_ID = "member-add-modal-id";

const MemberAddModal: React.FC = () => {
    const intl = useIntl();
    const modal = useModal();
    const scenarioId = useScenarioId();

    const [username, setUsername] = useState<string>("");
    const [role, setRole] = useState<Role>();
    const [validated, setValidated] = useState<boolean>(false);

    const { mutateAsync: createMember, isPending: isCreating } = useCreateMember(scenarioId);

    const validate = (form: any): form is CreateMember => {
        setValidated(true);
        if (!username) {
            return false;
        }
        if (!isEmail(username)){
            errorToast(intl.formatMessage({ id: "i11016" }));
            return false;
        }
        if (!role){
            return false;
        }
        return true;
    };

    const onClickConfirm = async () => {
        const form = {
            username: username,
            role_id: role?.id,
        };
        if (!validate(form)) {
            return;
        }

        await createMember(form)
            .then(() => {
                successToast(intl.formatMessage({ id: "i11009" }, { username }));
            })
            .catch(() => {
                errorToast(intl.formatMessage({ id: "t002" }));
            })
            .finally(() => {
                modal.close(MEMBER_ADD_MODAL_ID);
            });
    };

    const clear = () => {
        setUsername("");
        setRole(undefined);
        setValidated(false);
    };

    return (
        <Modal id={MEMBER_ADD_MODAL_ID}
               size="sm"
               title={intl.formatMessage({ id: "i11018" })}
               onClose={clear}
        >
            <div className="modal-body gap-y-8">
                <fieldset className="modal-description flex flex-col gap-4">
                    <legend className="sr-only">
                        {intl.formatMessage({ id: "i11018" })}
                    </legend>
                    <TextInputGroup defaultValue=""
                                    invalid={validated && !username}
                                    label={intl.formatMessage({ id: "i11019" })}
                                    placeholder={intl.formatMessage({ id: "i11008" })}
                                    onChange={setUsername}
                    />

                    <div className="flex flex-col gap-1.5">
                        <div className="field-label">
                            {intl.formatMessage({ id: "i11015" })}
                        </div>
                        <RoleDropDown aria-label={intl.formatMessage({ id: "i11015" })}
                                      onChange={setRole}
                        />
                    </div>
                </fieldset>

                <div className="modal-btn-group mt-8">
                    <Button size="sm"
                            variant="secondary"
                            onClick={() => modal.close(MEMBER_ADD_MODAL_ID)}
                    >
                        {intl.formatMessage({ id: "b0002" })}
                    </Button>
                    <Button size="sm"
                            variant="primary"
                            disabled={isCreating}
                            onClick={onClickConfirm}
                    >
                        {intl.formatMessage({ id: "b0001" })}
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default MemberAddModal;